/**
 * DataExportPage — `/service-desk/data-export`.
 *
 * Slice B10: wired to POST /v1/service-desk/exports. Polls job status
 * until READY, then surfaces a download link.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Download, FileJson, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';

import { createExportJob, getExportDownloadUrl, getExportJob } from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';

const FORM_KEYS = [
  '', 'NIMC_NIN_SLIP_REPRINT', 'FRSC_LICENCE_RENEWAL', 'FIRS_TIN_VERIFICATION', 'NPC_DEATH_CERT',
];

export function DataExportPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [formKey, setFormKey] = useState('');
  const [from, setFrom] = useState('2026-04-01');
  const [to, setTo] = useState('2026-05-01');
  const [format, setFormat] = useState<'CSV' | 'JSON'>('CSV');
  const [jobId, setJobId] = useState<string | null>(null);

  const triggerMut = useMutation({
    mutationFn: () =>
      createExportJob({ formKey: formKey || undefined, fromDate: from, toDate: to, format }),
    onSuccess: (job) => {
      setJobId(job.id);
    },
  });

  // Poll job status every 2 seconds while PENDING
  const jobQuery = useQuery({
    queryKey: ['service-desk', 'exports', jobId],
    queryFn: () => getExportJob(jobId ?? ''),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' ? 2000 : false;
    },
  });

  function reset(): void {
    setJobId(null);
    void queryClient.removeQueries({ queryKey: ['service-desk', 'exports'] });
    triggerMut.reset();
  }

  const job = jobQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader title="Data export" description="Export platform data in CSV / JSON." />

      {triggerMut.isError ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
          Export trigger failed: {triggerMut.error instanceof Error ? triggerMut.error.message : 'Unknown error'}
        </div>
      ) : null}

      {/* Job status banner */}
      {job ? (
        <div
          className={`rounded-xl border p-4 flex items-start gap-3 ${
            job.status === 'READY'
              ? 'bg-green-50 border-green-200'
              : job.status === 'FAILED'
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
          }`}
        >
          {job.status === 'PENDING' ? (
            <Loader2 className="w-5 h-5 text-amber-600 animate-spin mt-0.5" />
          ) : job.status === 'READY' ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : null}
          <div className="flex-1">
            <p className="text-sm font-semibold">
              {job.status === 'PENDING'
                ? 'Generating export...'
                : job.status === 'READY'
                  ? 'Export ready'
                  : 'Export failed'}
            </p>
            <p className="text-xs text-muted mt-0.5">Job ID: {job.id}</p>
          </div>
          {job.status === 'READY' ? (
            <a
              href={getExportDownloadUrl(job.id)}
              download={`export-${job.id}.${job.format.toLowerCase()}`}
              className="px-3 py-2 bg-nipost-green text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          ) : null}
          <button type="button" onClick={reset} className="text-xs text-muted underline ml-2">
            New export
          </button>
        </div>
      ) : null}

      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-muted mb-1">Form key (optional)</label>
          <select
            value={formKey}
            onChange={(e) => setFormKey(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
            disabled={!!jobId}
          >
            {FORM_KEYS.map((k) => (
              <option key={k} value={k}>{k || 'All forms'}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              disabled={!!jobId}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              disabled={!!jobId}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Format</label>
            <div className="flex items-center gap-2">
              {([
                { v: 'CSV',  I: FileText },
                { v: 'JSON', I: FileJson },
              ] as const).map((opt) => {
                const Icon = opt.I;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFormat(opt.v)}
                    disabled={!!jobId}
                    className={`px-3 py-2 rounded-lg border text-xs font-medium inline-flex items-center gap-1 ${
                      format === opt.v
                        ? 'border-nipost-green bg-nipost-green-light text-nipost-green'
                        : 'border-border'
                    } disabled:opacity-50`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {opt.v}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => triggerMut.mutate()}
          disabled={triggerMut.isPending || !!jobId}
          className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {triggerMut.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-4 h-4" />
          )}
          {triggerMut.isPending ? 'Triggering...' : 'Export'}
        </button>
      </div>
    </div>
  );
}
